import React, { useState } from 'react';
import { Search, TrendingUp } from 'lucide-react';
import apiService from '../apiService';

const TYPES = [
  { key: 'all', label: 'Tous' },
  { key: 'client', label: 'Clients' },
  { key: 'driver', label: 'Chauffeurs' },
  { key: 'supplier', label: 'Fournisseurs' },
];

const TYPE_BADGE = {
  client: { label: 'Client', bg: '#FEE9E2', color: '#F54119' },
  driver: { label: 'Chauffeur', bg: '#ECFDF5', color: '#10B981' },
  supplier: { label: 'Fournisseur', bg: '#fef3c7', color: '#FF9B00' },
};

// L'ajustement passe par l'endpoint propre à chaque type (même corps { amount, direction, note })
const ADJUST_URL = {
  client: (id) => `/admin/client-balance/${id}/adjust`,
  driver: (id) => `/balance/driver/${id}/adjust`,
  supplier: (id) => `/balance/admin/supplier/${id}/adjust`,
};

export default function ClientBalancePage() {
  const [searchType, setSearchType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null); // { type, id, name, phone }
  const [detail, setDetail] = useState(null);      // { current_balance, stats, transactions }
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustNote, setAdjustNote] = useState('');
  const [adjustType, setAdjustType] = useState('credit');
  const [adjusting, setAdjusting] = useState(false);

  const doSearch = async () => {
    if (!searchQuery.trim() || searchQuery.trim().length < 2) return;
    setSearching(true);
    try {
      const data = await apiService.get(`/admin/balance-search?q=${encodeURIComponent(searchQuery.trim())}&type=${searchType}`, { userType: 'admin' });
      if (data.success) setSearchResults(data.results || []);
    } catch (e) { console.error('Search error:', e); }
    finally { setSearching(false); }
  };

  const loadDetail = async (type, id) => {
    setLoadingBalance(true);
    setDetail(null);
    try {
      const data = await apiService.get(`/admin/balance/${type}/${id}`, { userType: 'admin' });
      if (data.success) setDetail(data);
    } catch (e) { alert('Erreur: ' + e.message); }
    finally { setLoadingBalance(false); }
  };

  const selectAccount = (r) => { setSelected(r); loadDetail(r.type, r.id); };

  const handleAdjust = async () => {
    const amount = parseInt(adjustAmount);
    if (!amount || amount <= 0) { alert('Montant invalide'); return; }
    if (!adjustNote.trim()) { alert('Veuillez saisir un motif'); return; }
    if (!window.confirm(`${adjustType === 'credit' ? 'Créditer' : 'Débiter'} ${amount.toLocaleString('fr-FR')} CDF ${adjustType === 'credit' ? 'au' : 'du'} compte de ${selected.name} ?\n\nMotif: ${adjustNote.trim()}`)) return;
    setAdjusting(true);
    try {
      const data = await apiService.post(ADJUST_URL[selected.type](selected.id), { direction: adjustType, amount, note: adjustNote.trim() }, { userType: 'admin' });
      if (data.success) {
        alert(`✅ ${adjustType === 'credit' ? 'Crédit' : 'Débit'} de ${amount.toLocaleString('fr-FR')} CDF effectué.`);
        setAdjustAmount(''); setAdjustNote('');
        loadDetail(selected.type, selected.id);
      }
    } catch (e) { alert('Erreur: ' + e.message); }
    finally { setAdjusting(false); }
  };

  const formatAmount = (n) => (Number(n) || 0).toLocaleString('fr-FR');
  const txnTypeLabel = (type) => {
    const map = {
      topup: 'Recharge', ride_payment: 'Course', ride_payment_partial: 'Paiement partiel',
      cancellation_fee: 'Frais annulation', refund: 'Remboursement', debt_recovery: 'Recouvrement',
      admin_adjustment: 'Ajustement admin', sale_credit: 'Vente', commission_debit: 'Commission',
      payout: 'Virement', cash_sale: 'Vente cash',
    };
    return map[type] || type;
  };

  const cardStyle = { background: 'white', borderRadius: '14px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: '16px' };

  return (
    <div className="dashboard-content">
      <h1 className="page-title">Soldes</h1>

      <div style={cardStyle}>
        {/* Sélecteur de type */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
          {TYPES.map(t => (
            <button key={t.key} onClick={() => setSearchType(t.key)}
              style={{ padding: '7px 16px', borderRadius: '20px', border: searchType === t.key ? '1.5px solid #FFB900' : '1px solid #E9EBEF', background: searchType === t.key ? '#FFF7ED' : '#FFFFFF', color: searchType === t.key ? '#C9350F' : '#5C636E', fontWeight: '600', fontSize: '0.85rem', cursor: 'pointer' }}>
              {t.label}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <Search size={20} color="#9AA0A8" />
          <input type="text" placeholder="Rechercher (nom, téléphone, ID)..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && doSearch()}
            style={{ flex: 1, padding: '10px 14px', borderRadius: '10px', border: '1px solid #E9EBEF', fontSize: '0.95rem' }} />
          <button onClick={doSearch} disabled={searching} style={{ padding: '10px 20px', borderRadius: '10px', border: 'none', background: '#FFB900', color: 'white', fontWeight: '600', cursor: 'pointer', opacity: searching ? 0.5 : 1 }}>Rechercher</button>
        </div>

        {searchResults.length > 0 && (
          <div style={{ marginTop: '16px' }}>
            {searchResults.map(r => {
              const badge = TYPE_BADGE[r.type] || {};
              return (
                <div key={`${r.type}-${r.id}`} onClick={() => selectAccount(r)} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', borderRadius: '10px', cursor: 'pointer', borderBottom: '1px solid #F4F5F7' }}>
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#F4F5F7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', color: '#9AA0A8', fontSize: '0.9rem' }}>{r.name?.charAt(0) || '?'}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600', color: '#1C1C1C', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {r.name}
                      <span style={{ background: badge.bg, color: badge.color, padding: '1px 8px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: '700' }}>{badge.label}</span>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#5C636E' }}>{r.phone} • {r.id}</div>
                  </div>
                  <div style={{ fontWeight: '700', color: (r.current_balance || 0) >= 0 ? '#10b981' : '#DC2626' }}>{formatAmount(r.current_balance)} CDF</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {selected && (
        <>
          {loadingBalance ? (
            <div className="loading-spinner"><div className="spinner"></div><p>Chargement du solde...</p></div>
          ) : detail ? (
            <>
              <div style={{ ...cardStyle, display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <div style={{ fontWeight: '700', fontSize: '1.1rem', color: '#1C1C1C', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {detail.name}
                    {TYPE_BADGE[selected.type] && (
                      <span style={{ background: TYPE_BADGE[selected.type].bg, color: TYPE_BADGE[selected.type].color, padding: '1px 8px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: '700' }}>{TYPE_BADGE[selected.type].label}</span>
                    )}
                  </div>
                  <div style={{ color: '#5C636E', fontSize: '0.9rem' }}>{detail.phone} • {selected.id}</div>
                </div>
                <div style={{ textAlign: 'center', minWidth: '150px' }}>
                  <div style={{ fontSize: '0.85rem', color: '#5C636E' }}>Solde actuel</div>
                  <div style={{ fontSize: '1.8rem', fontWeight: '800', color: detail.current_balance >= 0 ? '#10b981' : '#DC2626' }}>{formatAmount(detail.current_balance)} CDF</div>
                </div>
                {(detail.stats || []).map((s, i) => (
                  <div key={i} style={{ textAlign: 'center', minWidth: '120px' }}>
                    <div style={{ fontSize: '0.85rem', color: '#5C636E' }}>{s.label}</div>
                    <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#414852' }}>{formatAmount(s.value)} CDF</div>
                  </div>
                ))}
              </div>

              <div style={cardStyle}>
                <h3 style={{ margin: '0 0 1rem', color: '#414852' }}>Ajustement du solde</h3>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                  <button onClick={() => setAdjustType('credit')} style={{ flex: 1, padding: '10px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontWeight: '600', background: adjustType === 'credit' ? '#10b981' : '#F4F5F7', color: adjustType === 'credit' ? 'white' : '#5C636E' }}>+ Créditer</button>
                  <button onClick={() => setAdjustType('debit')} style={{ flex: 1, padding: '10px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontWeight: '600', background: adjustType === 'debit' ? '#DC2626' : '#F4F5F7', color: adjustType === 'debit' ? 'white' : '#5C636E' }}>- Débiter</button>
                </div>
                <input type="number" placeholder="Montant (CDF)" value={adjustAmount} onChange={(e) => setAdjustAmount(e.target.value)}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #E9EBEF', fontSize: '1rem', marginBottom: '10px', boxSizing: 'border-box' }} />
                <input type="text" placeholder="Motif / Note (obligatoire)" value={adjustNote} onChange={(e) => setAdjustNote(e.target.value)}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #E9EBEF', fontSize: '0.95rem', marginBottom: '12px', boxSizing: 'border-box' }} />
                <button onClick={handleAdjust} disabled={adjusting} style={{ width: '100%', padding: '12px', borderRadius: '12px', border: 'none', cursor: 'pointer', fontWeight: '700', fontSize: '1rem', background: adjustType === 'credit' ? '#10b981' : '#DC2626', color: 'white', opacity: adjusting ? 0.5 : 1 }}>
                  {adjusting ? 'En cours...' : `${adjustType === 'credit' ? 'Créditer' : 'Débiter'} ${adjustAmount ? formatAmount(parseInt(adjustAmount)) + ' CDF' : ''}`}
                </button>
              </div>

              <div style={cardStyle}>
                <h3 style={{ margin: '0 0 1rem', color: '#414852' }}>Historique des transactions</h3>
                {(detail.transactions || []).length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#9AA0A8', padding: '2rem' }}>Aucune transaction</div>
                ) : (
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {detail.transactions.map((txn, i) => {
                      const isCredit = txn.direction === 'credit';
                      return (
                        <div key={txn._id || i} style={{ display: 'flex', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #F4F5F7' }}>
                          <div style={{ width: 36, height: 36, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: isCredit ? '#d1fae5' : '#fee2e2', marginRight: '12px' }}>
                            <TrendingUp size={18} color={isCredit ? '#10b981' : '#DC2626'} style={isCredit ? {} : { transform: 'rotate(180deg)' }} />
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: '600', color: '#1C1C1C', fontSize: '0.9rem' }}>{txnTypeLabel(txn.type)}</div>
                            <div style={{ fontSize: '0.8rem', color: '#9AA0A8' }}>
                              {txn.created_at ? new Date(txn.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                              {txn.note ? ` • ${txn.note}` : ''}
                            </div>
                          </div>
                          <div style={{ fontWeight: '700', color: isCredit ? '#10b981' : '#DC2626', fontSize: '0.95rem' }}>{isCredit ? '+' : '-'}{formatAmount(txn.amount)} CDF</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          ) : null}
        </>
      )}
    </div>
  );
}
